import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DoranCalendar } from '@doranjs/react';
import { formatValue, toDoranDate, type DoranDate } from '@doranjs/core';
import './doran-date-picker.scss';
import { useDoranFormikBridge } from './doran-field-shell';
import { useDoranYearFirstNavigation } from './doran-picker-drilldown';
import {
    DoranGoToToday,
    DoranModalDialog,
    DoranModalTrigger,
} from './doran-modal-field';

export default function PersianDatePicker(props: any): JSX.Element {
    const bridge = useDoranFormikBridge(props);
    const {
        label,
        icon,
        disabled,
        showClearBtn,
        disableOpenWhenValue,
        ltr,
        mindate,
        maxdate,
        minDate,
        maxDate,
        headerMode = 'dropdown',
        yearSpan,
        initialPickerView = 'day',
    } = props;
    const [open, setOpen] = useState(false);
    const calendarContainerRef = useRef<HTMLDivElement>(null);

    const selectedDate = useMemo(
        () => toDoranDate(bridge.value || null),
        [bridge.value],
    );
    const [draftDate, setDraftDate] = useState<DoranDate | null>(selectedDate);
    const min = useMemo(
        () => toDoranDate(mindate ?? minDate),
        [mindate, minDate],
    );
    const max = useMemo(
        () => toDoranDate(maxdate ?? maxDate),
        [maxdate, maxDate],
    );

    const isDisabled =
        Boolean(disabled) ||
        (Boolean(disableOpenWhenValue) && Boolean(bridge.value));

    const currentYear = useMemo(() => {
        const today = toDoranDate(new Date());
        if (!today) return 0;

        const formatted = String(formatValue(today, 'YYYY') ?? '');
        return Number(
            formatted
                .replace(/[۰-۹]/g, (digit) =>
                    String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)),
                )
                .replace(/[٠-٩]/g, (digit) =>
                    String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)),
                ),
        );
    }, []);

    const handleCalendarClickCapture = useDoranYearFirstNavigation({
        containerRef: calendarContainerRef,
        open,
        enabled: initialPickerView === 'year',
        currentYear,
    });

    const openCalendar = () => {
        if (isDisabled) return;
        setDraftDate(selectedDate);
        setOpen(true);
    };

    useEffect(() => {
        if (isDisabled && open) {
            setOpen(false);
        }
    }, [isDisabled, open]);

    const selectDate = (nextDate: DoranDate | null) => {
        setDraftDate(nextDate);
        if (!nextDate) return;
        if (max && nextDate.epochMs > max.epochMs) return;

        bridge.setValue(String(formatValue(nextDate, 'YYYY/MM/DD') ?? ''));
        setOpen(false);
    };

    const clearDate = () => {
        bridge.setValue('');
        setDraftDate(null);
    };

    return (
        <>
            <DoranModalTrigger
                name={bridge.name}
                label={label}
                value={bridge.value || ''}
                icon={icon}
                disabled={isDisabled}
                invalid={bridge.invalid}
                helperText={bridge.helperText}
                open={open}
                onOpen={openCalendar}
                onClear={showClearBtn === false ? undefined : clearDate}
                onBlur={bridge.onBlur}
                ltr={ltr !== false}
            />

            <DoranModalDialog
                open={open}
                title={label || 'انتخاب تاریخ'}
                onClose={() => setOpen(false)}>
                <div
                    ref={calendarContainerRef}
                    className="project-doran-dialog__calendar-wrap"
                    onClickCapture={handleCalendarClickCapture}>
                    <DoranCalendar
                        value={draftDate}
                        onChange={selectDate}
                        headerMode={headerMode}
                        {...(yearSpan ? { yearSpan } : {})}
                        footerActions={[]}
                        slots={{ footer: <DoranGoToToday /> }}
                        {...(min ? { min } : {})}
                        {...(max ? { max } : {})}
                    />
                </div>
            </DoranModalDialog>
        </>
    );
}
